'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { useCategoryFilter } from '@lib/hooks/useCategoryFilter';
import { useCategorySelectorState } from '@lib/hooks/useCategorySelectorState';
import type { CategoryMetadata } from '@lib/types/category';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import {
  getCategoryDisplayName,
  getCategoryIconName,
} from '@lib/util/category';
import { c } from '@ui-components/utils/tailwind-merge';
import { DynamicIcon } from 'phosphor-icons/react';
import { createElement, useContext, useMemo, useState } from 'react';
import { TreeAccordion, TreeNodeData } from 'ui-components';
import type { CategorySelectorSectionProps } from './CategorySelectorSection.types';

const ALL_PRODUCTS_ID = 'all-products';

function findNodeById(
  items: TreeNodeData<CategoryMetadata>[],
  targetId: string
): TreeNodeData<CategoryMetadata> | null {
  for (const item of items) {
    if (item.id === targetId) {
      return item;
    }
    if (item.children) {
      const found = findNodeById(item.children, targetId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Recursively enhance tree nodes with icons and display names.
 * Uses full path as ID to ensure uniqueness (e.g., "3d/environments" vs "2d/environments").
 */
function enhanceTreeWithIconsAndLabels(
  nodes: TreeNodeData<CategoryMetadata>[],
  searchValuesMicrocopy?: MicrocopyItem[],
  parentPath: string[] = []
): TreeNodeData<CategoryMetadata>[] {
  return nodes.map((node) => {
    const currentPath = [...parentPath, node.id];
    const iconName = getCategoryIconName(node.id);

    return {
      ...node,
      id: currentPath.join('/'),
      label: getCategoryDisplayName(node.id, searchValuesMicrocopy),
      icon: iconName
        ? createElement(DynamicIcon, {
            iconName,
            size: 24,
            className: 'text-blue',
          })
        : undefined,
      children: node.children
        ? enhanceTreeWithIconsAndLabels(
            node.children,
            searchValuesMicrocopy,
            currentPath
          )
        : undefined,
    };
  });
}

export const CategorySelectorSection = ({
  context,
  microcopy,
  className,
}: CategorySelectorSectionProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const { searchEngine } = useContext(context);

  const { treeData, isLoading, error, categoryController } =
    useCategorySelectorState(context);

  const { applyFilter, clearFilter, currentCategoryPath } =
    useCategoryFilter(searchEngine);

  const enhancedTreeData = useMemo(
    () => enhanceTreeWithIconsAndLabels(treeData, searchValuesMicrocopy),
    [treeData, searchValuesMicrocopy]
  );

  const [manuallyExpandedIds, setManuallyExpandedIds] = useState<string[]>([]);
  const [manuallyCollapsedIds, setManuallyCollapsedIds] = useState<string[]>(
    []
  );

  const expandedIds = useMemo(() => {
    if (currentCategoryPath.length === 0) {
      return manuallyExpandedIds;
    }

    // Build ancestor path IDs (e.g., ["3d", "3d/characters"] for path ["3d", "characters", "humanoid"])
    const ancestorIds = currentCategoryPath
      .slice(0, -1)
      .map((_, i) => currentCategoryPath.slice(0, i + 1).join('/'));

    // Filter out manually collapsed ancestors
    const autoExpandedAncestors = ancestorIds.filter(
      (id) => !manuallyCollapsedIds.includes(id)
    );

    return Array.from(
      new Set([...manuallyExpandedIds, ...autoExpandedAncestors])
    );
  }, [currentCategoryPath, manuallyExpandedIds, manuallyCollapsedIds]);

  function handleExpandedChange(newExpandedIds: string[]): void {
    // Find which IDs were collapsed (were in expandedIds but not in newExpandedIds)
    const collapsedIds = expandedIds.filter(
      (id) => !newExpandedIds.includes(id)
    );
    // Find which IDs were expanded (are in newExpandedIds but weren't in expandedIds)
    const newlyExpandedIds = newExpandedIds.filter(
      (id) => !expandedIds.includes(id)
    );

    // Update manually collapsed IDs
    setManuallyCollapsedIds((prev) => {
      // Add newly collapsed IDs
      const updated = [...prev, ...collapsedIds];
      // Remove IDs that were just expanded
      return updated.filter((id) => !newlyExpandedIds.includes(id));
    });

    setManuallyExpandedIds(newExpandedIds);
  }

  function handleSelect(id: string): void {
    if (id === ALL_PRODUCTS_ID) {
      clearFilter();
      categoryController?.deselectAll();
      setManuallyCollapsedIds([]);
      return;
    }

    const node = findNodeById(enhancedTreeData, id);
    if (node?.metadata?.fullPath) {
      // Clear manually collapsed IDs so new ancestors can auto-expand
      setManuallyCollapsedIds([]);
      applyFilter(node.metadata.fullPath);
    }
  }

  const categoriesMicrocopy =
    microcopy?.categoriesLabel ||
    searchLabelsMicrocopy?.find((m) => m.key === 'search.categories')?.value ||
    'Categories';

  const allProductsMicrocopy = microcopy?.allProductsLabel || 'All Products';

  const selectedId =
    currentCategoryPath.length > 0
      ? currentCategoryPath.join('/')
      : ALL_PRODUCTS_ID;

  if (isLoading) {
    return (
      <div className={c('mt-4 lg:mt-1', className)}>
        <div className="tiny-text mb-2 font-semibold uppercase tracking-wide text-grey-800">
          {categoriesMicrocopy}
        </div>
        <div className="animate-pulse">
          <div className="mb-2 h-8 rounded bg-grey-100" />
          <div className="mb-2 h-8 rounded bg-grey-100" />
          <div className="h-8 rounded bg-grey-100" />
        </div>
      </div>
    );
  }

  if (error) {
    console.error('Category Selector Error:', error);
    return null;
  }

  return (
    <div className={className}>
      <div className="tiny-text mb-1 font-semibold uppercase tracking-wide text-grey-800">
        {categoriesMicrocopy}
      </div>

      <div className="category-selector-wrapper">
        {currentCategoryPath.length > 0 && (
          <button
            onClick={() => handleSelect(ALL_PRODUCTS_ID)}
            className={c(
              'flex w-full items-center gap-3 px-1 py-2 text-left',
              'transition-colors duration-150',
              'rounded-lg',
              'hover:bg-grey-100',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-hover'
            )}
          >
            <DynamicIcon
              iconName="CaretLeft"
              size={16}
              className="text-grey-600"
            />
            <DynamicIcon
              iconName="SquaresFour"
              size={24}
              className="text-blue"
            />
            <span className="flex-1 text-base">{allProductsMicrocopy}</span>
          </button>
        )}

        <TreeAccordion
          items={enhancedTreeData}
          selectedId={selectedId}
          onSelect={handleSelect}
          expandedIds={expandedIds}
          onExpandedChange={handleExpandedChange}
        />
      </div>
    </div>
  );
};
